import { Loader2 } from 'lucide-react';
import React, { useCallback, useMemo, useRef } from 'react';

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useCaseHistory } from '@/hooks/useCaseHistory';
import { useSchema } from '@/hooks/useSchema';
import type { FieldGroup, FormField } from '@/types/formSchema';
import { computePreviousValuesMap } from '@/utils/caseHistoryDiff';

import { CaseHistoryEntry } from './CaseHistoryEntry';
import { CaseHistoryErrorBoundary } from './CaseHistoryErrorBoundary';

interface CaseHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseRecordId: string;
  workflowId?: string;
  /** The case's nomos schema major, so history labels match the schema the case was written under. */
  schemaVersion?: string;
}

/**
 * Build a flat map of field IDs to their full schema definitions.
 * Includes label, type, options (for select/enum resolution), etc.
 */
function buildFieldSchemaMap(groups: FieldGroup[]): Record<string, FormField> {
  const map: Record<string, FormField> = {};
  for (const group of groups) {
    for (const field of group.fields) {
      map[field.id] = field;
    }
  }
  return map;
}

const HistoryLoadingSkeleton: React.FC = () => (
  <div className="relative">
    <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
    <div className="space-y-6">
      {[1, 2, 3].map(i => (
        <div key={i} className="relative flex items-start gap-3">
          <Skeleton className="size-10 rounded-full shrink-0 relative z-10" />
          <div className="flex-1 pt-1">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24 mt-1.5" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div className="text-center py-8 text-muted-foreground">
    <p className="text-sm">No history found</p>
    <p className="text-xs mt-1">Changes to this case will appear here</p>
  </div>
);

/**
 * Sheet component to display case record history with field-level diffs.
 * Uses infinite scroll to load more entries, with eager prefetch to support
 * delta-to-state reconstruction needed by the diff renderer.
 */
export function CaseHistorySheet({
  open,
  onOpenChange,
  caseRecordId,
  workflowId,
  schemaVersion,
}: CaseHistorySheetProps) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    entries,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    allLoaded,
    usersMap,
    isLoadingUsers,
  } = useCaseHistory({
    caseRecordId,
    enabled: open,
  });

  // Fetch schema to resolve field labels for output_data / input_data diffs
  const { inputGroups, outputGroups } = useSchema(workflowId, schemaVersion);
  const fieldSchemaMap = useMemo(
    () => buildFieldSchemaMap([...inputGroups, ...outputGroups]),
    [inputGroups, outputGroups],
  );

  // Compute previous values for each delta entry once all history is loaded
  const previousValuesMap = useMemo(
    () => (allLoaded ? computePreviousValuesMap(entries) : new Map()),
    [entries, allLoaded],
  );

  // Mutable ref to always have the latest load-more function without recreating observer
  const loadMoreFnRef = useRef(() => {});
  loadMoreFnRef.current = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (!node) return;

    observerRef.current = new IntersectionObserver(
      observed => {
        if (observed[0].isIntersecting) {
          loadMoreFnRef.current();
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 },
    );
    observerRef.current.observe(node);
  }, []);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[450px] sm:w-[500px] p-0 flex flex-col"
        onPointerDownOutside={e => e.stopPropagation()}
        onInteractOutside={e => e.stopPropagation()}
        onCloseAutoFocus={e => e.preventDefault()}
      >
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle className="text-base font-semibold">Case History</SheetTitle>
        </SheetHeader>

        <CaseHistoryErrorBoundary>
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 py-3">
            {(isLoading || isLoadingUsers) && entries.length === 0 ? (
              <HistoryLoadingSkeleton />
            ) : error ? (
              <div className="text-center py-8 text-destructive">
                <p className="text-sm">Failed to load history</p>
              </div>
            ) : entries.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />
                <div className="space-y-6">
                  {entries.map(entry => (
                    <CaseHistoryEntry
                      key={entry.id}
                      entry={entry}
                      user={
                        entry.actor?.user?.user_id
                          ? usersMap.get(entry.actor.user.user_id)
                          : undefined
                      }
                      fieldSchemaMap={fieldSchemaMap}
                      previousValues={entry.id ? previousValuesMap.get(entry.id) : undefined}
                    />
                  ))}
                </div>

                <div ref={sentinelRef} className="h-4">
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="text-xs text-muted-foreground ml-2">Loading more...</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CaseHistoryErrorBoundary>
      </SheetContent>
    </Sheet>
  );
}
